import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { userService, siteService } from '../../services';
import { companyService } from '../../services/company.service';
import type { Company } from '../../services/company.service';
import { User, Site } from '../../types';
import { USER_ROLES, USER_ROLE_LABELS, DEFAULT_PAGE_SIZE } from '../../constants';
import { usePagination } from '../../hooks';
import { toast } from '../../components/Toast';
import styles from './Users.module.css';

export const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);

  // Use pagination custom hook
  const { page, totalPages, setTotalPages, resetPage, nextPage, previousPage, isFirstPage, isLastPage, hasMultiplePages } = usePagination();

  // Filtri
  const [roleFilter, setRoleFilter] = useState<number | ''>('');
  const [activeFilter, setActiveFilter] = useState<boolean | ''>('');
  const [siteFilter, setSiteFilter] = useState<number | ''>('');
  const [companyFilter, setCompanyFilter] = useState<number | ''>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: USER_ROLES.EMPLOYEE as number,
    companyId: currentUser?.companyId,
    siteId: currentUser?.siteId,
    isActive: true
  });

  // Memoize loadUsers to prevent recreation on every render
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const filter: {
        page: number;
        pageSize: number;
        role?: number;
        active?: boolean;
        search?: string;
        siteId?: number;
        companyId?: number;
      } = { page, pageSize: DEFAULT_PAGE_SIZE.USERS };

      if (roleFilter !== '') {
        filter.role = roleFilter;
      }

      if (activeFilter !== '') {
        filter.active = activeFilter;
      }

      if (searchTerm) {
        filter.search = searchTerm;
      }

      if (siteFilter !== '') {
        filter.siteId = siteFilter;
      }

      if (companyFilter !== '') {
        filter.companyId = companyFilter;
      }

      const response = await userService.getUsers(filter);
      setUsers(response.data);
      setTotalPages(response.totalPages);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel caricamento degli utenti';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [page, roleFilter, activeFilter, siteFilter, companyFilter, searchTerm, setTotalPages]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const isSuperAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN;

  // Carica lista aziende (solo SuperAdmin)
  useEffect(() => {
    if (!isSuperAdmin) return;
    const loadCompanies = async () => {
      try {
        const response = await companyService.getCompanies({ pageSize: 100 });
        setCompanies(response.data);
      } catch (err: unknown) {
        console.error('Errore nel caricamento aziende:', err);
      }
    };
    loadCompanies();
  }, [isSuperAdmin]);

  // Carica lista sedi disponibili (in base alla company selezionata per SuperAdmin)
  const loadSites = useCallback(async (cId?: number) => {
    try {
      const targetCompanyId = cId ?? currentUser?.companyId;
      if (!targetCompanyId && !isSuperAdmin) return;
      const response = await siteService.getSites({
        companyId: targetCompanyId,
        isActive: true,
        pageSize: 100,
      });
      setSites(response.data);
    } catch (err: unknown) {
      console.error('Errore nel caricamento delle sedi:', err);
    }
  }, [currentUser?.companyId, isSuperAdmin]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  // Memoize initial form data
  const initialFormData = useMemo(
    () => ({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: USER_ROLES.EMPLOYEE as number,
      companyId: currentUser?.companyId,
      siteId: currentUser?.siteId,
      isActive: true,
    }),
    [currentUser?.companyId, currentUser?.siteId]
  );

  // Memoize handleSearch
  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      resetPage();
      loadUsers();
    },
    [resetPage, loadUsers]
  );

  // Memoize openCreateModal
  const openCreateModal = useCallback(() => {
    setEditingUser(null);
    setFormData(initialFormData);
    setShowModal(true);
  }, [initialFormData]);

  // Memoize openEditModal
  const openEditModal = useCallback((user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      password: '',
      role: user.role,
      companyId: user.companyId,
      siteId: user.siteId,
      isActive: user.isActive,
    });
    setShowModal(true);
  }, []);

  // Memoize handleSubmit
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');

      try {
        if (editingUser) {
          // Update existing user
          const payload: {
            firstName: string;
            lastName: string;
            email: string;
            role: number;
            companyId?: number;
            siteId?: number;
            isActive: boolean;
            password?: string;
          } = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            role: formData.role,
            companyId: formData.companyId,
            siteId: formData.siteId,
            isActive: formData.isActive,
          };

          // Only include password if it was changed
          if (formData.password) {
            payload.password = formData.password;
          }

          await userService.updateUser(editingUser.id, payload);
          toast.success('Utente aggiornato con successo!');
        } else {
          // Create new user
          await userService.createUser(formData);
          toast.success('Utente creato con successo!');
        }

        setShowModal(false);
        loadUsers();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Errore nel salvataggio dell'utente";
        setError(message);
      }
    },
    [editingUser, formData, loadUsers]
  );

  // Memoize handleDelete
  const handleDelete = useCallback(
    async (id: number) => {
      if (!window.confirm('Sei sicuro di voler disattivare questo utente?')) {
        return;
      }

      try {
        await userService.deactivateUser(id);
        toast.success('Utente disattivato con successo');
        loadUsers();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Errore nella disattivazione dell'utente";
        toast.error(message);
      }
    },
    [loadUsers]
  );

  // Memoize getRoleName using constants
  const getRoleName = useCallback((role: number) => {
    return USER_ROLE_LABELS[role] || 'Unknown';
  }, []);

  // Memoize form field change handlers
  const handleFormChange = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  // Memoize filter change handlers
  const handleRoleFilterChange = useCallback(
    (value: string) => {
      setRoleFilter(value === '' ? '' : parseInt(value));
      resetPage();
    },
    [resetPage]
  );

  const handleActiveFilterChange = useCallback(
    (value: string) => {
      setActiveFilter(value === '' ? '' : value === 'true');
      resetPage();
    },
    [resetPage]
  );

  const handleSiteFilterChange = useCallback(
    (value: string) => {
      setSiteFilter(value === '' ? '' : parseInt(value));
      resetPage();
    },
    [resetPage]
  );

  return (
    <div className={styles.users}>
      <div className={styles.header}>
        <h1>Gestione Utenti</h1>
        <button className={styles.createButton} onClick={openCreateModal}>
          + Nuovo Utente
        </button>
      </div>

      <div className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Cerca per nome o email..."
            className={styles.searchInput}
          />
          <button type="submit" className={styles.searchButton}>
            Cerca
          </button>
        </form>

        <div className={styles.filterGroup}>
          <label htmlFor="role">Ruolo:</label>
          <select
            id="role"
            value={roleFilter}
            onChange={(e) => handleRoleFilterChange(e.target.value)}
            className={styles.select}
          >
            <option value="">Tutti</option>
            <option value={USER_ROLES.SUPER_ADMIN}>Super Admin</option>
            <option value={USER_ROLES.OWNER}>Owner</option>
            <option value={USER_ROLES.MANAGER}>Manager</option>
            <option value={USER_ROLES.EMPLOYEE}>Employee</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="active">Stato:</label>
          <select
            id="active"
            value={activeFilter.toString()}
            onChange={(e) => handleActiveFilterChange(e.target.value)}
            className={styles.select}
          >
            <option value="">Tutti</option>
            <option value="true">Attivi</option>
            <option value="false">Disattivati</option>
          </select>
        </div>

        {isSuperAdmin && (
          <div className={styles.filterGroup}>
            <label htmlFor="company">Azienda:</label>
            <select
              id="company"
              value={companyFilter}
              onChange={(e) => {
                const val = e.target.value === '' ? '' : parseInt(e.target.value);
                setCompanyFilter(val);
                setSiteFilter('');
                resetPage();
                if (val !== '') loadSites(val as number);
              }}
              className={styles.select}
            >
              <option value="">Tutte le aziende</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className={styles.filterGroup}>
          <label htmlFor="site">Sede:</label>
          <select
            id="site"
            value={siteFilter}
            onChange={(e) => handleSiteFilterChange(e.target.value)}
            className={styles.select}
          >
            <option value="">Tutte le sedi</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          ❌ {error}
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>Caricamento utenti...</div>
      ) : users.length === 0 ? (
        <div className={styles.empty}>
          <p>📭 Nessun utente trovato</p>
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Ruolo</th>
                  <th>Azienda</th>
                  <th>Sede</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className={!user.isActive ? styles.inactive : ''}>
                    <td>{user.id}</td>
                    <td>
                      {user.firstName} {user.lastName}
                    </td>
                    <td>{user.email}</td>
                    <td>{getRoleName(user.role)}</td>
                    <td>{user.companyName}</td>
                    <td>{user.siteName}</td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${
                          user.isActive ? styles.active : styles.inactiveStatus
                        }`}
                      >
                        {user.isActive ? '✅ Attivo' : '❌ Disattivato'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.editButton}
                          onClick={() => openEditModal(user)}
                        >
                          ✏️
                        </button>
                        <button
                          className={styles.deleteButton}
                          onClick={() => handleDelete(user.id)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMultiplePages && (
            <div className={styles.pagination}>
              <button
                onClick={previousPage}
                disabled={isFirstPage}
                className={styles.pageButton}
              >
                ← Precedente
              </button>
              <span className={styles.pageInfo}>
                Pagina {page} di {totalPages}
              </span>
              <button
                onClick={nextPage}
                disabled={isLastPage}
                className={styles.pageButton}
              >
                Successiva →
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal Form */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingUser ? 'Modifica Utente' : 'Nuovo Utente'}</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowModal(false)}
              >
                ❌
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="firstName">Nome:</label>
                  <input
                    type="text"
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleFormChange('firstName', e.target.value)}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="lastName">Cognome:</label>
                  <input
                    type="text"
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleFormChange('lastName', e.target.value)}
                    className={styles.input}
                    required
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="password">
                  Password {editingUser && '(lascia vuoto per non modificare)'}:
                </label>
                <input
                  type="password"
                  id="password"
                  value={formData.password}
                  onChange={(e) => handleFormChange('password', e.target.value)}
                  className={styles.input}
                  required={!editingUser}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="role">Ruolo:</label>
                  <select
                    id="role"
                    value={formData.role}
                    onChange={(e) =>
                      handleFormChange('role', parseInt(e.target.value))
                    }
                    className={styles.select}
                    required
                  >
                    {currentUser && currentUser.role === USER_ROLES.SUPER_ADMIN && (
                      <option value={USER_ROLES.SUPER_ADMIN}>Super Admin</option>
                    )}
                    {currentUser && currentUser.role <= USER_ROLES.OWNER && (
                      <option value={USER_ROLES.OWNER}>Owner</option>
                    )}
                    {currentUser && currentUser.role <= USER_ROLES.MANAGER && (
                      <option value={USER_ROLES.MANAGER}>Manager</option>
                    )}
                    <option value={USER_ROLES.EMPLOYEE}>Employee</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="isActive">Stato:</label>
                  <select
                    id="isActive"
                    value={formData.isActive.toString()}
                    onChange={(e) =>
                      handleFormChange('isActive', e.target.value === 'true')
                    }
                    className={styles.select}
                    required
                  >
                    <option value="true">Attivo</option>
                    <option value="false">Disattivato</option>
                  </select>
                </div>
              </div>

              {/* Campo Azienda - per SuperAdmin */}
              {isSuperAdmin && formData.role !== USER_ROLES.SUPER_ADMIN && (
                <div className={styles.formGroup}>
                  <label htmlFor="companyId">Azienda *</label>
                  <select
                    id="companyId"
                    value={formData.companyId || ''}
                    onChange={(e) => {
                      const cId = e.target.value ? parseInt(e.target.value) : undefined;
                      handleFormChange('companyId', cId);
                      handleFormChange('siteId', undefined);
                      if (cId) loadSites(cId);
                    }}
                    className={styles.select}
                    required
                  >
                    <option value="">-- Seleziona Azienda --</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Campo Sede - obbligatorio per Manager/Employee */}
              {(formData.role === USER_ROLES.MANAGER || formData.role === USER_ROLES.EMPLOYEE) && (
                <div className={styles.formGroup}>
                  <label htmlFor="siteId">Sede *</label>
                  <select
                    id="siteId"
                    value={formData.siteId || ''}
                    onChange={(e) =>
                      handleFormChange('siteId', e.target.value ? parseInt(e.target.value) : undefined)
                    }
                    className={styles.select}
                    required
                  >
                    <option value="">-- Seleziona Sede --</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name} - {site.city}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => setShowModal(false)}
                >
                  Annulla
                </button>
                <button type="submit" className={styles.submitButton}>
                  {editingUser ? '💾 Salva Modifiche' : '➕ Crea Utente'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

Users.displayName = 'Users';
